import { z } from 'zod'

import { MessagingType } from 'apps/posting/constants/MessagingType'
import { Status } from 'apps/posting/models/Post/constants'
import { MessageTagType } from 'common/builder/constants/facebookMessaging'
import { NotificationType } from 'common/core/constants/NotificationType'
import { ProErrorSchema } from 'shared/api/common/schemas/errors'
import { FilterSchema } from 'shared/api/common/schemas/filters'
import { LightFlowSchema } from 'shared/api/common/schemas/flow/lightFlow'
import { fsFlowObjectBaseSchema } from 'shared/api/requests/cms/schemas/fsFlowObjectBaseSchema'
import { messageTemplate } from 'shared/api/requests/whatsApp/schemas'

import { FilterErrorSchema, PostErrorSchema } from './errors'

const PostSendingStatsSchema = z.object({
  messages_total: z.number().nullable(),
  messages_processed: z.number().nullable(),
  messages_sent: z.number().nullable(),
  messages_skipped: z.number().nullable(),
  messages_failed: z.number().nullable(),
  messages_blocked: z.number().nullable(),
})

const PostStatsSchema = z.object({
  clicked: z.number().nullable(),
  delivered: z.number().nullable(),
  read: z.number().nullable(),
  sent: z.number().nullable(),
})

enum PostFlowType {
  POST_DRAFT = 'post_draft',
  POST_PROCESSED = 'post_processed',
  POST_AWAIT = 'post_await',
  NEED_ACTIONS = 'need_actions',
}

const PostDependencySchema = z.object({
  message_templates: z.union([z.array(messageTemplate), z.record(z.string(), messageTemplate)]),
})

const PostFlowSchema = fsFlowObjectBaseSchema.extend({
  type: z.enum(PostFlowType),
  flow: LightFlowSchema.optional(),
  path: z.string().optional(),
  template_installation_id: z.number().nullable().optional(),
  sending_stats: z.number().optional(),
  deleted: z.number().nullable().optional(),
  title: z.string().optional(),
  triggers: z.looseObject({}).optional(),
  folder_id: z.number().optional(),
  created: z.number().optional(),
  modified: z.number().optional(),
})

const PostSchema = z.object({
  post_id: z.number(),
  flow: PostFlowSchema,
  preview: z.string(),
  status: z.enum(Status),
  timestamp: z.number(),
  ts_scheduled: z.number(),
  namespace: z.string(),
  notification: z.enum(NotificationType),
  timezone: z.object({
    type: z.string(),
  }),
  messaging_type: z.enum(MessagingType),
  message_tag: z.union([z.enum(MessageTagType), z.string()]).nullable(),
  content_holder: z.string(),
  schedule: z.number().nullable(),
  source: z.string(),
  dependencies: PostDependencySchema.nullable(),
  stats: PostStatsSchema.nullable().optional(),
  sending_stats: PostSendingStatsSchema.nullable().optional(),
  filter: FilterSchema.nullable(),
})

const PostListResponseSchema = z.object({
  limiter: z.union([z.number(), z.string()]).nullable().optional(),
  posts: z.array(PostSchema),
  total: z.number(),
})

export const BroadcastingApiSchemas = {
  loadScheduled: {
    path: z.undefined(),
    query: z.object({
      limiter: z.union([z.number(), z.string()]).nullable().optional(),
    }),
    request: z.undefined(),
    response: PostListResponseSchema,
  },
  loadHistory: {
    path: z.undefined(),
    query: z.object({
      limiter: z.union([z.number(), z.string()]).nullable().optional(),
    }),
    request: z.undefined(),
    response: PostListResponseSchema,
  },
  loadDrafts: {
    path: z.undefined(),
    query: z.object({
      limiter: z.union([z.number(), z.string()]).nullable().optional(),
    }),
    request: z.undefined(),
    response: PostListResponseSchema,
  },
  copy: {
    path: z.undefined(),
    query: z.object({
      post_id: z.union([z.string(), z.number()]),
    }),
    request: z.undefined(),
    response: z.object({
      post: PostSchema,
    }),
    errors: {
      ProError: ProErrorSchema,
    },
  },
  create: {
    path: z.undefined(),
    query: z.undefined(),
    request: z
      .object({
        flow_ns: z.string(),
      })
      .optional(),
    response: z.object({
      post: PostSchema,
    }),
    errors: {
      PostError: PostErrorSchema,
    },
  },
  schedule: {
    path: z.undefined(),
    query: z.object({
      post_id: z.union([z.string(), z.number()]),
    }),
    request: z.undefined(),
    response: z.object({
      post: PostSchema,
    }),
    errors: {
      PostError: PostErrorSchema,
    },
  },
  unschedule: {
    path: z.undefined(),
    query: z.object({
      post_id: z.union([z.string(), z.number()]),
    }),
    request: z.undefined(),
    response: z.object({
      post: PostSchema,
    }),
  },
  interrupt: {
    path: z.undefined(),
    query: z.object({
      post_id: z.union([z.string(), z.number()]),
    }),
    request: z.undefined(),
    response: z.object({
      post: PostSchema,
    }),
  },
  forceSending: {
    path: z.undefined(),
    query: z.object({
      post_id: z.union([z.string(), z.number()]),
    }),
    request: z.undefined(),
    response: z.object({
      post: PostSchema,
    }),
    errors: {
      PostError: PostErrorSchema,
    },
  },
  update: {
    path: z.undefined(),
    query: z.object({
      post_id: z.union([z.string(), z.number()]),
    }),
    request: z.object({
      filter: z.looseObject({}).nullable(),
      messaging_type: z.string(),
      notification: z.string(),
      schedule: z.number().nullable(),
      timezone: z.object({
        type: z.string(),
      }),
      message_tag: z.string().nullable(),
      current_time: z.number(),
      current_time_offset: z.number(),
    }),
    response: z.object({
      post: PostSchema,
    }),
    errors: {
      FilterError: FilterErrorSchema,
      PostError: PostErrorSchema,
    },
  },
  load: {
    path: z.undefined(),
    query: z.object({
      post_id: z.union([z.string(), z.number()]),
    }),
    request: z.undefined(),
    response: z.object({
      post: PostSchema,
    }),
  },
  delete: {
    path: z.undefined(),
    query: z.object({
      post_id: z.union([z.string(), z.number()]),
    }),
    request: z.undefined(),
    response: z.object({
      post: PostSchema,
    }),
  },
}

export type Post = z.infer<typeof PostSchema>
export type PostSendingStats = z.infer<typeof PostSendingStatsSchema>
export type Filter = z.infer<typeof FilterSchema>
export type PostStats = z.infer<typeof PostStatsSchema>
export type PostDependency = z.infer<typeof PostDependencySchema>
